import clsx from 'clsx';
import dayjs from 'dayjs';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Avatar,
  ButtonColor,
  ButtonVariant,
  Icon,
  IconButton,
  IconComponent,
  TagsRow,
  Typography,
  TypographyType,
} from '../../../components';
import { CoachingTemplate, DateFormat, ScorecardTemplate, Tag, TemplateStatus, TextColor } from '../../../types';

type TemplateRowProps = {
  template: ScorecardTemplate | CoachingTemplate;
  isDuplicating: boolean;
  isApplyingTags: boolean;
  onApplyTags: (selectedTags: Tag[], setSelectedTags: (tags: Tag[]) => void) => Promise<void>;
  onDuplicateTemplate: (templateId: string) => void;
  icon?: IconComponent;
};

const TemplateRow = ({
  template,
  isApplyingTags = false,
  onApplyTags,
  onDuplicateTemplate,
  isDuplicating,
  icon,
}: TemplateRowProps) => {
  const navigate = useNavigate();
  const { id, name, tags = [], updatedAt, lastEditor, status } = template;
  const isTemplateArchived = status === TemplateStatus.ARCHIVED;

  const [selectedTags, setSelectedTags] = useState(tags);

  return (
    <div className="flex items-center justify-between gap-2">
      <div className={clsx('flex w-full flex-col gap-1 overflow-auto', isTemplateArchived && 'opacity-40')}>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Typography type={TypographyType.H4} className="max-w-[320px] overflow-hidden truncate" noWrap>
              {name}
            </Typography>
            {icon}
          </div>
          <TagsRow
            tags={tags}
            onApplyTags={() => onApplyTags(selectedTags, setSelectedTags)}
            isApplyingTags={isApplyingTags}
            selectedTags={selectedTags}
            setSelectedTags={setSelectedTags}
          />
        </div>

        <div className="flex items-center gap-2">
          {/* Last editor doesn't exist in org seeded coaching templates */}
          {lastEditor && (
            <>
              <Avatar size={16} label={lastEditor.name} imgSrc={lastEditor.picture} />
              <Typography type={TypographyType.X_SMALL} color={TextColor.SECONDARY}>
                {lastEditor.name}
              </Typography>
            </>
          )}
          <Typography type={TypographyType.X_SMALL} color={TextColor.SECONDARY}>
            {dayjs(updatedAt).format(DateFormat.MONTH_DAY_TIME)}
          </Typography>
        </div>
      </div>

      <div className="flex gap-1">
        <IconButton
          icon={Icon.EDIT}
          color={ButtonColor.NEUTRAL}
          variant={ButtonVariant.SOFT}
          onClick={() => navigate(id, { replace: true })}
          disabled={isTemplateArchived}
          tooltip="Edit"
        />
        <IconButton
          icon={Icon.DUPLICATE}
          color={ButtonColor.NEUTRAL}
          variant={ButtonVariant.SOFT}
          onClick={() => onDuplicateTemplate(id)}
          loading={isDuplicating}
          tooltip="Duplicate"
        />
      </div>
    </div>
  );
};

export default TemplateRow;
